import { useEffect, useReducer, useState } from "react";

import { Classes } from "@blueprintjs/core";
import { useMediaQuery } from "react-responsive";
import { toast } from "sonner";

import { Box, Flex } from "@/components/layout/flexbox";
import { isXsQuery } from "@/components/layout/Media";
import { TimezoneSelector } from "@/components/pages_logged_in/schedule/timeslots/TimezoneSelector";
import { WorkingTimeslot } from "@/components/pieces/coaching/CoachingSettings/Timeslots/WorkingTimeslot";
import { CARD_CLASSES, SUBTEXT_COLOR } from "@/css/constants";
import {
  CalendarSettingsDocument,
  CalendarSettingsQuery,
  useCalendarSettingsQuery,
  useSaveCalendarSettingsMutation,
} from "@/graphql";
import { ensureNonNullArray } from "@/utils/array";
import { getBrowserTimezone } from "@/utils/datetime";

import { DateOverridesEditor } from "./DateOverridesEditor";
import { SLOT_OPTIONS, weekDays } from "./timeslots";

type CalendarUser = NonNullable<CalendarSettingsQuery["currentUser"]>;

type WeekDay =
  | "mondayTimeslots"
  | "tuesdayTimeslots"
  | "wednesdayTimeslots"
  | "thursdayTimeslots"
  | "fridayTimeslots"
  | "saturdayTimeslots"
  | "sundayTimeslots";

interface DateCalendarAction {
  type: "SET_TIME_SLOT";
  value: string[];
  date: WeekDay;
}

interface TimeZoneCalendarAction {
  type: "SET_TIME_ZONE";
  value: string;
}

type CalendarAction = DateCalendarAction | TimeZoneCalendarAction;

type CalendarState = {
  timeZone: string;
} & {
  [key in WeekDay]: string[];
};

function reducer(state: CalendarState, action: CalendarAction) {
  switch (action.type) {
    case "SET_TIME_SLOT":
      return {
        ...state,
        [action.date]: action.value,
      };
    case "SET_TIME_ZONE":
      return {
        ...state,
        timeZone: action.value,
      };
    default:
      console.log("Unknown action: ", action);
      return state;
  }
}

interface CalendarSettingsProps {
  user: CalendarUser;
  onSettingChanged: () => void;
}

function CalendarSettings({ user, onSettingChanged }: CalendarSettingsProps) {
  const timeslotsSettingOn = (date: WeekDay) =>
    ensureNonNullArray(user.calendarSetting?.[date]);

  const isXs = useMediaQuery(isXsQuery);

  const [calendarState, dispatch] = useReducer(reducer, {
    timeZone: getBrowserTimezone(),
    mondayTimeslots: timeslotsSettingOn("mondayTimeslots"),
    tuesdayTimeslots: timeslotsSettingOn("tuesdayTimeslots"),
    wednesdayTimeslots: timeslotsSettingOn("wednesdayTimeslots"),
    thursdayTimeslots: timeslotsSettingOn("thursdayTimeslots"),
    fridayTimeslots: timeslotsSettingOn("fridayTimeslots"),
    saturdayTimeslots: timeslotsSettingOn("saturdayTimeslots"),
    sundayTimeslots: timeslotsSettingOn("sundayTimeslots"),
  });

  const [mutate, { loading }] = useSaveCalendarSettingsMutation({
    variables: {
      input: calendarState,
    },
    refetchQueries: [{ query: CalendarSettingsDocument }],
    awaitRefetchQueries: true,
    onError: () => {
      toast.error("An error occurred. Please try refreshing your browser.");
    },
    onCompleted: (response) => {
      if (!response.saveCalendarSettings?.ok) {
        toast.error("An error occurred. Please try refreshing your browser.");
      } else {
        if (onSettingChanged) {
          onSettingChanged();
        }
        toast.success("Saved");
      }
    },
  });

  function submit() {
    mutate();
  }

  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(true);
  }, []);
  useEffect(() => {
    if (ready) {
      submit();
    }
  }, [calendarState]);

  return (
    <>
      <Box
        mt={4}
        className={CARD_CLASSES}
        backgroundColor="#FFF"
        justifyContent="space-between"
        alignItems="center"
        py={[3, 3, 3, 24]}
        px={[3, 3, 3, 4]}
      >
        <Box fontSize={20} fontWeight={600}>
          Available Hours
        </Box>
        <Box color={SUBTEXT_COLOR}>Set your working hours for sessions</Box>

        <Flex justifyContent="space-between" flexDirection={["column", "row"]}>
          <Box flexBasis="60%" pr={[0, 4]}>
            <Box pb={24} pt={3}>
              <Box>
                <strong>Time Zone</strong>
              </Box>
              <Box mt={3}>
                <TimezoneSelector
                  value={calendarState.timeZone}
                  onChange={(timezone) => {
                    if (!timezone) return;
                    dispatch({
                      type: "SET_TIME_ZONE",
                      value: timezone.value,
                    });
                  }}
                />
              </Box>
            </Box>

            {weekDays.map((wd) => (
              <Box key={wd.value} py={24} className="bts">
                <WorkingTimeslot
                  date={wd.label}
                  loading={loading}
                  slotOptions={SLOT_OPTIONS}
                  timeslots={calendarState[wd.value]}
                  onChange={(timeslots) => {
                    dispatch({
                      type: "SET_TIME_SLOT",
                      date: wd.value,
                      value: timeslots,
                    });
                  }}
                />
              </Box>
            ))}
          </Box>
          <Box
            flexBasis="40%"
            className={isXs ? "bts" : "bls"}
            pt={[4, 0]}
            pl={[0, 4]}
          >
            <Box fontWeight={600} fontSize={[20, 16]}>
              Date Overrides
            </Box>
            <Box mt={2} color={SUBTEXT_COLOR}>
              Add dates when your availability is different than your standard
              hours
            </Box>
            <Box mt={3}>
              <DateOverridesEditor />
            </Box>
          </Box>
        </Flex>
      </Box>
    </>
  );
}

interface CalendarSettingsWrapperProps {
  onSettingChanged: () => void;
}

export function CalendarSettingsWrapper({
  onSettingChanged,
}: CalendarSettingsWrapperProps) {
  const { data, loading } = useCalendarSettingsQuery();

  if (!data || loading) {
    return (
      <Flex
        mt={4}
        className={`${Classes.ELEVATION_1} round-corners ${Classes.SKELETON}`}
        backgroundColor="#FFF"
        justifyContent="space-between"
        alignItems="center"
        py={[3, 3, 3, 24]}
        px={[3, 3, 3, 4]}
      />
    );
  }

  if (!data.currentUser) return null;

  return (
    <CalendarSettings
      user={data.currentUser}
      onSettingChanged={onSettingChanged}
    />
  );
}
